import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check } from "lucide-react";
import {
  formatMoney,
  productImageAlt,
  shopifyImg,
  shopifySrcSet,
  type ShopifyProduct,
} from "@/lib/shopify";
import { HERO_PICKS, heroPicksQueryOptions, type HeroPick } from "@/lib/hero-picks";
import { displayBrand, getProductFacts } from "@/lib/product-facts";
import { ga4ItemCategory, trackRailInteraction, trackSelectItem } from "@/lib/gtag";

const railKey = "vertailu";

type Row = { label: string; get: (pick: HeroPick, product: ShopifyProduct) => string | undefined };

/**
 * Comparison rows. Hard specs come from the canonical fact sheet — when a
 * field is missing for a product the cell shows "—" instead of a guess.
 */
const ROWS: Row[] = [
  {
    label: "Hinta",
    get: (_p, product) =>
      formatMoney(
        product.node.priceRange.minVariantPrice.amount,
        product.node.priceRange.minVariantPrice.currencyCode,
      ),
  },
  { label: "Ikäsuositus", get: (_p, product) => getProductFacts(product.node.handle).recommendedAge },
  { label: "Valmistaja", get: (_p, product) => getProductFacts(product.node.handle).manufacturer ?? displayBrand(product.node.vendor) ?? "—" },
  {
    label: "Valmistusmaa",
    get: (_p, product) => {
      const f = getProductFacts(product.node.handle);
      return f.manufacturingCountry ?? f.manufacturingRegion;
    },
  },
  { label: "Mitat", get: (_p, product) => getProductFacts(product.node.handle).dimensions },
  { label: "Maksimikuormitus", get: (_p, product) => getProductFacts(product.node.handle).maxLoad },
  { label: "Sopii parhaiten", get: (p) => p.useCase },
  { label: "Erottuu", get: (p) => p.shortDifferentiator },
  { label: "Tilantarve", get: (p) => p.space },
  { label: "Käyttöikä", get: (p) => p.lifespan },
  { label: "Materiaali", get: (_p, product) => getProductFacts(product.node.handle).material },
  {
    label: "Standardit",
    get: (_p, product) => getProductFacts(product.node.handle).standards?.join(" · "),
  },
];

type Entry = HeroPick & { index: number; product: ShopifyProduct };

function useEntries() {
  const q = useQuery(heroPicksQueryOptions);
  const byHandle = new Map((q.data ?? []).map((p) => [p.node.handle, p]));
  const entries = HERO_PICKS.map((p, index) => ({ ...p, index, product: byHandle.get(p.handle) })).filter(
    (e): e is Entry => Boolean(e.product && e.product.node.images.edges[0]?.node.url),
  );
  return { entries, isLoading: q.isLoading };
}

function trackClick(entry: Entry) {
  const node = entry.product.node;
  const variant = node.variants.edges[0]?.node;
  const price = node.priceRange.minVariantPrice;
  trackSelectItem({
    id: variant?.id ?? node.id,
    name: node.title,
    brand: displayBrand(node.vendor),
    category: ga4ItemCategory(node.title),
    price: parseFloat(price.amount),
    currency: price.currencyCode,
    listName: railKey,
    index: entry.index,
  });
  trackRailInteraction({
    rail: railKey,
    action: "product_click",
    product_id: node.id,
    product_name: node.title,
    position: entry.index,
  });
}

function PickHead({ entry }: { entry: Entry }) {
  const img = entry.product.node.images.edges[0].node;
  return (
    <Link
      to="/product/$handle"
      params={{ handle: entry.product.node.handle }}
      onClick={() => trackClick(entry)}
      className="group block"
    >
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-cream">
        <img
          src={shopifyImg(img.url, 480)}
          srcSet={shopifySrcSet(img.url, 480)}
          sizes="(min-width: 1024px) 22vw, 70vw"
          width={480}
          height={360}
          alt={productImageAlt(img.altText, entry.displayTitle)}
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-contain p-4 mix-blend-multiply transition-transform duration-500 group-hover:scale-[1.04] motion-reduce:transform-none"
        />
      </div>
      <p className="mt-4 inline-flex rounded-full bg-sage/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground">
        {entry.bestFor}
      </p>
      <h3 className="mt-2 font-display text-xl italic leading-tight text-foreground">
        {entry.displayTitle}
      </h3>
      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{entry.verdict}</p>
    </Link>
  );
}

function PickCta({ entry }: { entry: Entry }) {
  return (
    <Link
      to="/product/$handle"
      params={{ handle: entry.product.node.handle }}
      onClick={() => trackClick(entry)}
      className="inline-flex w-full items-center justify-center rounded-[12px] bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5 active:scale-[0.98]"
    >
      Katso tuote
    </Link>
  );
}

/**
 * Side-by-side comparison of the four products we actively recommend.
 * Desktop renders a real table (semantic + scannable); mobile renders one
 * card per pick so no horizontal scrolling is needed.
 */
export function ProductComparison({ headingId = "vertailu-title" }: { headingId?: string }) {
  const { entries, isLoading } = useEntries();
  if (isLoading) return <ComparisonSkeleton />;
  if (entries.length === 0) return null;

  return (
    <div>
      {/* Desktop: one column per product */}
      <div className="hidden lg:block">
        <table className="w-full table-fixed border-collapse text-left">
          <caption className="sr-only">
            Neljän suositellun Montessori-tuotteen vertailu: hinta, ikäsuositus, tilantarve ja materiaalit.
          </caption>
          <thead>
            <tr>
              <th scope="col" className="w-[16%] align-bottom pb-8 pr-6">
                <span className="sr-only">Ominaisuus</span>
              </th>
              {entries.map((e) => (
                <th key={e.handle} scope="col" className="w-[21%] align-top px-3 pb-8 font-normal">
                  <PickHead entry={e} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.label} className="border-t border-border">
                <th scope="row" className="py-4 pr-6 align-top text-[11px] font-semibold uppercase tracking-[0.16em] text-clay">
                  {row.label}
                </th>
                {entries.map((e) => (
                  <td key={e.handle} className="px-3 py-4 align-top text-sm leading-relaxed text-foreground">
                    {row.get(e, e.product) ?? <span className="text-muted-foreground">—</span>}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-border">
              <th scope="row" className="py-4 pr-6 align-top text-[11px] font-semibold uppercase tracking-[0.16em] text-clay">
                Valitse tämä jos
              </th>
              {entries.map((e) => (
                <td key={e.handle} className="px-3 py-4 align-top text-sm leading-relaxed text-foreground">
                  <span className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden />
                    {e.pickIf}
                  </span>
                </td>
              ))}
            </tr>
            <tr>
              <td className="pt-6" />
              {entries.map((e) => (
                <td key={e.handle} className="px-3 pt-6 align-top">
                  <PickCta entry={e} />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile / tablet: one card per pick, same rows stacked */}
      <div className="grid gap-6 md:grid-cols-2 lg:hidden">
        {entries.map((e) => (
          <article
            key={e.handle}
            className="rounded-3xl border border-border bg-card p-5"
            aria-labelledby={`${headingId}-${e.handle}`}
          >
            <div id={`${headingId}-${e.handle}`}>
              <PickHead entry={e} />
            </div>
            <dl className="mt-5 divide-y divide-border border-y border-border text-sm">
              {ROWS.map((row) => {
                const value = row.get(e, e.product);
                if (!value) return null;
                return (
                  <div key={row.label} className="flex gap-4 py-2.5">
                    <dt className="w-[38%] shrink-0 text-[11px] font-semibold uppercase tracking-[0.14em] text-clay">
                      {row.label}
                    </dt>
                    <dd className="flex-1 leading-relaxed text-foreground">{value}</dd>
                  </div>
                );
              })}
            </dl>
            <p className="mt-4 flex gap-2 text-sm leading-relaxed text-muted-foreground">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.75} aria-hidden />
              {e.pickIf}
            </p>
            <div className="mt-5">
              <PickCta entry={e} />
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function ComparisonSkeleton() {
  return (
    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-[4/3] rounded-2xl bg-sand/60" />
          <div className="mt-4 h-3 w-1/3 rounded bg-sand/60" />
          <div className="mt-3 h-4 w-3/4 rounded bg-sand/60" />
          <div className="mt-6 h-40 rounded-xl bg-sand/40" />
        </div>
      ))}
    </div>
  );
}

export default ProductComparison;
